import React from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const AdvancedSettings = ({ settings, onChange }) => {
  return (
    <div className="space-y-6">
      {/* Developer Mode */}
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <Icon name="Code" size={18} className="text-primary" />
          <h3 className="text-lg font-medium text-foreground">Developer Settings</h3>
        </div>
        
        <div className="space-y-4">
          <div className="bg-muted rounded-lg p-4">
            <Checkbox
              checked={settings?.developerMode}
              onChange={(e) => onChange('developerMode', e?.target?.checked)}
              label="Enable Developer Mode"
              description="Show technical details and debugging information in the interface"
            />
          </div>
          
          <div className="bg-muted rounded-lg p-4">
            <Checkbox
              checked={settings?.debugMode}
              onChange={(e) => onChange('debugMode', e?.target?.checked)}
              label="Debug Mode"
              description="Enable console logging and error details for troubleshooting"
            />
          </div>
        </div>
      </div>

      {/* Custom RPC Endpoint */}
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <Icon name="Server" size={18} className="text-primary" />
          <h3 className="text-lg font-medium text-foreground">Custom RPC Settings</h3>
        </div>
        
        <div className="space-y-4">
          <Input
            label="Custom RPC Endpoint"
            value={settings?.customRPC}
            onChange={(e) => onChange('customRPC', e?.target?.value)}
            placeholder="https://polygon-rpc.com"
            description="Override default RPC endpoint (leave empty for default)"
          />
        </div>
      </div>

      {/* Experimental Features */}
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <Icon name="Zap" size={18} className="text-primary" />
          <h3 className="text-lg font-medium text-foreground">Experimental Features</h3>
        </div>
        
        <div className="space-y-4">
          <div className="bg-muted rounded-lg p-4">
            <Checkbox
              checked={settings?.experimentalFeatures}
              onChange={(e) => onChange('experimentalFeatures', e?.target?.checked)}
              label="Enable Experimental Features"
              description="Access beta features and experimental functionality (may be unstable)"
            />
          </div>
        </div>
      </div>

      {/* Smart Contract Interactions */}
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <Icon name="FileCode" size={18} className="text-primary" />
          <h3 className="text-lg font-medium text-foreground">Smart Contract Settings</h3>
        </div>
        
        <div className="bg-muted rounded-lg p-4">
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Contract Version:</span>
                <span className="font-medium text-foreground">v2.1.0</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Network:</span>
                <span className="font-medium text-foreground">Polygon Mainnet</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Gas Optimization:</span>
                <span className="font-medium text-green-600">Enabled</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Batch Transactions:</span>
                <span className="font-medium text-blue-600">Available</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* System Information */}
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <Icon name="Info" size={18} className="text-primary" />
          <h3 className="text-lg font-medium text-foreground">System Information</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center space-x-3 mb-3">
              <Icon name="Monitor" size={20} className="text-blue-600" />
              <h4 className="font-medium text-foreground">Application</h4>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Version:</span>
                <span className="font-medium text-foreground">1.0.0</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Build:</span>
                <span className="font-medium text-foreground">2025.01.07</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Environment:</span>
                <span className="font-medium text-foreground">Production</span>
              </div>
            </div>
          </div>
          
          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center space-x-3 mb-3">
              <Icon name="Globe" size={20} className="text-green-600" />
              <h4 className="font-medium text-foreground">Network</h4>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Status:</span>
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                  <span className="font-medium text-green-600">Online</span>
                </div>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Block Height:</span>
                <span className="font-medium text-foreground">45,892,341</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Gas Price:</span>
                <span className="font-medium text-foreground">25 Gwei</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Warning Notice */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Icon name="AlertTriangle" size={18} className="text-yellow-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-yellow-900 mb-1">Advanced Settings Warning</h4>
            <p className="text-sm text-yellow-800">
              These settings are intended for advanced users. Incorrect configuration may affect the functionality of the application. Please proceed with caution.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvancedSettings;